/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import produce from 'immer';
import {localPersistence} from '@ion/core/src/capabilities/storage';
import {error} from '@ion/core/src/logger/logger';
import {GenericObject, User} from '@ion/core/src/types/entities';
import {decode} from '@ion/core/src/utils/Cipher';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import {
  APPLICATION_INITIALIZATION_COMPLETE, APPLICATION_INITIALIZATION_ERROR, INSUFFICIENT_PERMISSIONS_ERROR
} from '@/store/actions/initialization';
import {
  LOGIN_FAILED,
  LOGIN_REQUEST,
  LOGIN_SUCCEEDED,
  LOGOUT_FAILED,
  LOGOUT_REQUEST,
  MFA_PENDING,
  PERSIST_CREDENTIALS,
  UPDATE_CURRENT_USER
} from '@/store/actions/login';
import {ApplicationKeys} from '@/types/application';

/**
 * Provides the default credentials persisted to localStorage via the remember
 * me feature.
 *
 * @property emptyCredentials
 */
export const emptyCredentials = {
  defaultAccountId : null,
  rememberMe       : false,
  credentials      : {
    username : '',
    password : ''
  }
};

/**
 * @private
 */
const getPersisted = () => {
  const item = safeJSONParse(
    JSON.stringify(localPersistence(ApplicationKeys.iOnSuite, 'login', null) || emptyCredentials)
  );
  const {password} = item?.credentials;
  if (password) {
    try {
      item.credentials.password = decode(password);
    } catch (err) {
      error(err.toString());
    }
  }
  return item;
};

type PasswordChangeState = {
  inProgress: boolean;
  isComplete: boolean;
  isError: boolean;
};

/**
 * Provides a utility which retrieves the users persisted credentials from
 * localStorage (if available), Otherwise, returns the default emptyCredentials.
 *
 * @method persisted
 */
const persisted = getPersisted();

// defines the shape of this reducers state ...
export interface LoginState {
  isAuthenticating: boolean;
  authenticated: boolean;
  isServiceError: boolean;
  isInvalid: boolean;
  persistCredentials: boolean;
  hasPreviewFeature: boolean;
  isSuiteOnly: boolean;
  user: GenericObject;
  account: object;
  passwordChange: PasswordChangeState;
  isForbidden: boolean;
}

// defines the initial state for the reducer ...
export const initialState: LoginState = {
  isAuthenticating   : false,
  authenticated      : false,
  isServiceError     : false,
  isInvalid          : false,
  hasPreviewFeature  : false,
  isSuiteOnly        : false,
  isForbidden        : false,
  persistCredentials : persisted.rememberMe,
  user               : persisted.credentials,
  account            : {},
  passwordChange     : {
    inProgress : false,
    isComplete : false,
    isError    : null
  }
};

// defines the reducers ...
const reducers = {
  [LOGIN_REQUEST] : (draft: LoginState) => {
    draft.isAuthenticating = true;
    draft.isServiceError = draft.isInvalid = draft.authenticated = draft.isForbidden = false;
  },
  [LOGIN_SUCCEEDED] : (draft: LoginState, {user, account}) => {
    draft.isAuthenticating = draft.isServiceError = draft.isInvalid = draft.isForbidden = false;
    draft.user    = user;
    draft.account = account;
  },
  [LOGIN_FAILED] : (draft: LoginState, {isInvalid}) => {
    draft.isInvalid = isInvalid;
    draft.isServiceError = !isInvalid;
    draft.authenticated = draft.isAuthenticating = draft.isForbidden = false;
  },
  [MFA_PENDING] : (draft: LoginState) => {
    draft.isAuthenticating = draft.isServiceError = draft.isInvalid = draft.isForbidden = false;
  },
  [PERSIST_CREDENTIALS] : (draft: LoginState, {persist}) => {
    draft.persistCredentials = persist;
  },
  [LOGOUT_REQUEST] : (draft: LoginState) => {
    draft.isAuthenticating = true;
  },
  [LOGOUT_FAILED] : (draft: LoginState) => {
    draft.isAuthenticating = false;
  },
  [APPLICATION_INITIALIZATION_COMPLETE] : (draft: LoginState) => {
    draft.hasPreviewFeature = false;
    draft.isSuiteOnly       = true;
    draft.authenticated     = true;
    draft.isForbidden       = false;
  },
  [INSUFFICIENT_PERMISSIONS_ERROR] : (draft: LoginState) => {
    draft.hasPreviewFeature = draft.isSuiteOnly = false;
  },
  [UPDATE_CURRENT_USER] : (draft: LoginState, {updated}: {updated: User}) => {
    draft.user = updated;
  },
  [APPLICATION_INITIALIZATION_ERROR] : (draft: LoginState, {isForbidden}) => {
    draft.authenticated = draft.isAuthenticating = false;
    draft.isForbidden = isForbidden;
  }
};

// defines all reducers for actions of interest to the this reducer ...
export default produce((draft: LoginState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
